import { useEffect, type ReactNode } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import { useAtom } from "jotai";
import { languageAtom } from "@/state/language";
import enMessages from "@/locales/en";
import nlMessages from "@/locales/nl";
import { useUser } from "@/hooks/useUser";
import { useUpdateUser } from "@/hooks/auth-hooks";

// Messages for each supported language
const messages = {
  en: enMessages,
  nl: nlMessages,
};

export const IntlProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useUser();
  const { mutateAsync } = useUpdateUser();
  const [language, setLanguage] = useAtom(languageAtom);

  // Ensure we have a valid language, fallback to 'en'
  const currentLanguage = language in messages ? (language as keyof typeof messages) : "en";
  const currentMessages = messages[currentLanguage];

  // Initially, if the user doesnt have a language set, set it to the default language
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user?.language) {
      mutateAsync({
        language,
      });
    }
  }, [isAuthenticated, user?.language, language, setLanguage, mutateAsync]);

  // Keep the languageAtom in sync with the user's language
  useEffect(() => {
    if (!isAuthenticated) return;

    if (user?.language && language !== user?.language) {
      setLanguage(user?.language);
    }
  }, [isAuthenticated, language, setLanguage, user?.language]);

  return (
    <ReactIntlProvider locale={currentLanguage} messages={currentMessages} defaultLocale="en">
      {children}
    </ReactIntlProvider>
  );
};
